/* ============================================================
   <mps-stat-drawer> — bottom sheet (mobile) / modal (desktop)
   for the clickable stat tiles in main-product-section's
   product_stats block. Mounted once per page. Content is cloned
   from a sibling <template data-stat-template> at open time —
   no innerHTML construction, no price/DOM building.
   ============================================================ */

class MpsStatDrawer extends HTMLElement {
  connectedCallback() {
    /* Shell dedup — keep the first shell active, disable any extras. */
    if (window.__mpsStatdActive) {
      this.setAttribute('hidden', '');
      return;
    }
    window.__mpsStatdActive = this;

    this.contentEl  = this.querySelector('[data-stat-content]');
    this.scrollEl   = this.querySelector('[data-stat-scroll]');
    this.backdropEl = this.querySelector('[data-stat-backdrop]');
    this.sheetEl    = this.querySelector('.mps-statd__sheet');
    this.titleEl    = null;

    this._lastFocusedTrigger = null;

    this._bindGlobalTriggers();
    this._bindCloseInteractions();
  }

  /* ---- Open from any [data-stat-trigger] anywhere on the page ---- */
  _bindGlobalTriggers() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-stat-trigger]');
      if (!trigger) return;
      e.preventDefault();
      this.open(trigger);
    });
  }

  _bindCloseInteractions() {
    this.querySelectorAll('[data-stat-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.backdropEl.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  /* ---- Open ---- */
  open(trigger) {
    const tplKey = trigger.dataset.statTplKey;
    const tpl = document.querySelector(
      `template[data-stat-template][data-stat-tpl-key="${CSS.escape(tplKey)}"]`
    );
    if (!tpl) {
      console.warn('[mps-statd] template not found for', tplKey);
      return;
    }

    this._lastFocusedTrigger = trigger;

    /* Inject pre-rendered content (cloned from <template>). */
    this.contentEl.replaceChildren(tpl.content.cloneNode(true));

    /* Label the dialog by its title for screen readers. */
    const titleEl = this.contentEl.querySelector('.mps-statd__title');
    if (titleEl) this.setAttribute('aria-label', titleEl.textContent.trim());

    if (this.scrollEl) this.scrollEl.scrollTop = 0;

    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    /* Force a reflow so the transition kicks in from the off-screen state. */
    void this.offsetWidth;
    this.classList.add('is-open');

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      const closeBtn = this.querySelector('[data-stat-close]');
      if (closeBtn) closeBtn.focus();
    });
  }

  /* ---- Close ---- */
  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');

    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';

    const finish = () => {
      this.setAttribute('hidden', '');
      this.contentEl.replaceChildren();
      if (this._lastFocusedTrigger) {
        try { this._lastFocusedTrigger.focus(); } catch (_) {}
        this._lastFocusedTrigger = null;
      }
    };

    if (this.sheetEl) {
      let done = false;
      const onEnd = () => {
        if (done) return;
        done = true;
        this.sheetEl.removeEventListener('transitionend', onEnd);
        finish();
      };
      this.sheetEl.addEventListener('transitionend', onEnd);
      /* Fallback in case transitionend doesn't fire (e.g. reduced motion). */
      setTimeout(onEnd, 360);
    } else {
      finish();
    }
  }
}

if (!customElements.get('mps-stat-drawer')) {
  customElements.define('mps-stat-drawer', MpsStatDrawer);
}
